/*
 * Tabs, two kinds:
 *   <Tabs defaultValue="a" items={[{ value: 'a', label: 'A', content: … }]} />   local state (Radix)
 *   <RouteTabs items={[{ to: 'overview', label: … }, { to: 'key', label: … }]} /> one tab per subroute
 * RouteTabs uses NavLink, so `/servers/:id/key` highlights "key"; put <Outlet /> under it. Both scroll
 * horizontally on narrow screens instead of wrapping: the active tab scrolls into view, and an edge fades
 * out on the side where more tabs are hidden.
 */
import { Tabs as T } from 'radix-ui'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react'
import { NavLink, useLocation } from 'react-router'
import { cn } from '@/lib/cn'

const FADE = 28

/**
 * A horizontally scrolling row: keeps the active item (`aria-current="page"` or `data-state="active"`) in
 * view when `activeKey` changes, and fades the edge where items are cut off (a CSS mask, so it works on any
 * background).
 */
function useScrollRow(ref: RefObject<HTMLElement | null>, activeKey: string | undefined) {
  const [edges, setEdges] = useState({ start: false, end: false, rtl: false })

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const rtl = getComputedStyle(el).direction === 'rtl'
    const max = el.scrollWidth - el.clientWidth
    const pos = Math.abs(el.scrollLeft)
    const next = { start: pos > 1, end: max - pos > 1, rtl }
    setEdges((e) => (e.start === next.start && e.end === next.end && e.rtl === next.rtl ? e : next))
  }, [ref])

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const active = el.querySelector<HTMLElement>('[aria-current="page"],[data-state="active"]')
    if (active) {
      const box = el.getBoundingClientRect()
      const a = active.getBoundingClientRect()
      // physical deltas work in both directions
      if (a.left < box.left + FADE) el.scrollBy({ left: a.left - box.left - FADE })
      else if (a.right > box.right - FADE) el.scrollBy({ left: a.right - box.right + FADE })
    }
    measure()
  }, [ref, activeKey, measure])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.addEventListener('scroll', measure, { passive: true })
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', measure)
      ro.disconnect()
    }
  }, [ref, measure])

  const left = edges.rtl ? edges.end : edges.start
  const right = edges.rtl ? edges.start : edges.end
  const mask =
    left || right
      ? `linear-gradient(to right, transparent 0, #000 ${left ? FADE : 0}px, #000 calc(100% - ${right ? FADE : 0}px), transparent 100%)`
      : undefined
  const style: CSSProperties | undefined = mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined
  return style
}

const listClasses = 'flex items-center gap-1 overflow-x-auto border-b border-hairline scrollbar-none'
const tabClasses =
  'relative inline-flex h-10 shrink-0 items-center gap-2 rounded-t-lg px-3 text-14 font-medium whitespace-nowrap text-muted transition-colors hover:text-ink touch:min-h-11 [&_svg]:size-4 after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-transparent after:transition-colors'
const activeClasses = 'text-ink after:bg-brand-600'

export type TabItem = {
  value: string
  label: ReactNode
  icon?: ReactNode
  count?: number
  content: ReactNode
  disabled?: boolean
}

export function Tabs({
  items,
  value,
  defaultValue,
  onValueChange,
  className,
  'aria-label': ariaLabel,
}: {
  items: TabItem[]
  value?: string
  defaultValue?: string
  onValueChange?: (v: string) => void
  className?: string
  'aria-label'?: string
}) {
  const [current, setCurrent] = useState(value ?? defaultValue ?? items[0]?.value)
  const listRef = useRef<HTMLDivElement>(null)
  const rowStyle = useScrollRow(listRef, value ?? current)
  return (
    <T.Root
      value={value}
      defaultValue={defaultValue ?? items[0]?.value}
      onValueChange={(v) => {
        setCurrent(v)
        onValueChange?.(v)
      }}
      className={className}
    >
      <T.List ref={listRef} style={rowStyle} aria-label={ariaLabel} className={listClasses}>
        {items.map((i) => (
          <T.Trigger
            key={i.value}
            value={i.value}
            disabled={i.disabled}
            className={cn(
              tabClasses,
              'disabled:opacity-50 data-[state=active]:text-ink data-[state=active]:after:bg-brand-600',
            )}
          >
            {i.icon}
            {i.label}
            {i.count !== undefined ? (
              <span className="rounded-full bg-control px-1.5 figures text-12 text-muted">{i.count}</span>
            ) : null}
          </T.Trigger>
        ))}
      </T.List>
      {items.map((i) => (
        <T.Content key={i.value} value={i.value} className="pt-6 outline-none">
          {i.content}
        </T.Content>
      ))}
    </T.Root>
  )
}

export function RouteTabs({
  items,
  className,
  'aria-label': ariaLabel,
}: {
  items: { to: string; label: ReactNode; icon?: ReactNode; end?: boolean; count?: number }[]
  className?: string
  'aria-label'?: string
}) {
  const { pathname } = useLocation()
  const navRef = useRef<HTMLElement>(null)
  const rowStyle = useScrollRow(navRef, pathname)
  return (
    <nav ref={navRef} style={rowStyle} aria-label={ariaLabel} className={cn(listClasses, className)}>
      {items.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.end}
          className={({ isActive }) => cn(tabClasses, isActive && activeClasses)}
        >
          {i.icon}
          {i.label}
          {i.count !== undefined ? (
            <span className="rounded-full bg-control px-1.5 figures text-12 text-muted">{i.count}</span>
          ) : null}
        </NavLink>
      ))}
    </nav>
  )
}
